import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils.js";

/* Pill buttons, molded: a top light, a bottom edge, a real drop shadow —
   pressing sinks the button 1px and flattens the edge. */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold transition-[background-color,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:translate-y-px [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[linear-gradient(180deg,#cd3ac7_0%,#bd27b7_45%,#9c1f97_100%)] text-primary-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.35),inset_0_-2px_0_rgb(0_0_0/0.22),0_2px_5px_rgb(156_31_151/0.35)] hover:brightness-105 active:shadow-[inset_0_1px_0_rgb(255_255_255/0.25),inset_0_-1px_0_rgb(0_0_0/0.2),0_1px_2px_rgb(156_31_151/0.3)]",
        outline:
          "border border-border bg-[linear-gradient(180deg,#ffffff_0%,#f2f2f2_100%)] shadow-[inset_0_1px_0_rgb(255_255_255/0.9),inset_0_-2px_0_rgb(0_0_0/0.08),0_2px_4px_rgb(0_0_0/0.12)] hover:brightness-[0.98] active:shadow-[inset_0_1px_0_rgb(255_255_255/0.7),inset_0_-1px_0_rgb(0_0_0/0.08),0_1px_2px_rgb(0_0_0/0.1)] dark:bg-[linear-gradient(180deg,#28242d_0%,#201d24_100%)] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.06),inset_0_-2px_0_rgb(0_0_0/0.3),0_2px_4px_rgb(0_0_0/0.4)] dark:active:shadow-[inset_0_1px_0_rgb(255_255_255/0.04),inset_0_-1px_0_rgb(0_0_0/0.25),0_1px_2px_rgb(0_0_0/0.3)]",
        ghost: "hover:bg-muted",
        destructive:
          "bg-[linear-gradient(180deg,#d64a28_0%,#c13515_50%,#a02b10_100%)] text-destructive-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.3),inset_0_-2px_0_rgb(0_0_0/0.22),0_2px_5px_rgb(160_43_16/0.35)] hover:brightness-105 active:shadow-[inset_0_1px_0_rgb(255_255_255/0.2),inset_0_-1px_0_rgb(0_0_0/0.2),0_1px_2px_rgb(160_43_16/0.3)]",
        link: "text-foreground underline underline-offset-4 hover:text-primary",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 px-3.5 text-xs",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}
